import api, { CHAT_API_URL } from './api';

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

const STUN_FALLBACK: IceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

/**
 * Fetch the ICE server list (STUN/TURN) the RTCPeerConnection should use.
 * Falls back to Google's public STUN if the backend is unreachable so a call
 * can still attempt a same-network P2P connection.
 */
export async function getIceServers(): Promise<IceServer[]> {
  try {
    const r = await api.get<{ iceServers: IceServer[] }>('/calls/ice-servers');
    return r.data?.iceServers ?? STUN_FALLBACK;
  } catch {
    return STUN_FALLBACK;
  }
}

export type CallDirection = 'incoming' | 'outgoing';
export type CallType = 'voice' | 'video';
export type CallOutcome =
  | 'completed' | 'missed' | 'no_answer' | 'declined' | 'busy' | 'cancelled' | 'failed';

export interface CallLogCreate {
  call_id: string;
  peer_user_id: string | null;
  conversation_id?: string | null;
  direction: CallDirection;
  call_type: CallType;
  outcome: CallOutcome;
  started_at?: string | null;
  answered_at?: string | null;
  ended_at?: string | null;
  duration_s: number;
}

export interface CallEventCreate {
  call_id: string;
  conversation_id: string;
  call_type: CallType;
  outcome: CallOutcome;
  is_group: boolean;
  duration_s: number;
  started_at?: string | null;
  ended_at?: string | null;
  peer_user_id?: string | null;
}

export interface CallLogRecord {
  id: string;
  call_id: string;
  peer_user_id: string | null;
  peer_name: string | null;
  peer_avatar_url: string | null;
  conversation_id: string | null;
  direction: CallDirection;
  call_type: CallType;
  outcome: CallOutcome;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  duration_s: number;
  created_at: string | null;
  is_group?: boolean;
}

/** Persist a finalized call record (idempotent server-side on call_id+owner). */
export const logCall = (payload: CallLogCreate) =>
  api.post<CallLogRecord>('/calls/log', payload).then(r => r.data);

export const logCallEvent = (payload: CallEventCreate) =>
  api.post('/calls/event', payload).then(r => r.data);

/** Fetch the viewer's call history, newest first. */
export const getCallHistory = (params?: { before_id?: string; limit?: number }) =>
  api.get<CallLogRecord[]>('/calls/history', { params }).then(r => r.data);

/** Soft-delete one row. */
export const deleteCallLog = (logId: string) =>
  api.delete(`/calls/log/${encodeURIComponent(logId)}`).then(() => undefined);

/** Clear the whole call log. */
export const clearCallHistory = () =>
  api.delete('/calls/history').then(() => undefined);

/**
 * Unload-safe variant of logCall: native fetch with keepalive:true so the
 * request survives the page closing (axios/XHR would be aborted). Replicates
 * the axios auth header (CHAT_API_URL already ends in /chat-api).
 */
export function logCallKeepalive(payload: CallLogCreate): void {
  try {
    const token = localStorage.getItem('accessToken');
    void fetch(`${CHAT_API_URL}/calls/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch { /* noop */ }
}

export interface ScheduledCallRecord {
  id: string;
  conversation_id: string | null;
  scheduled_at: string | null;   // UTC ISO with trailing 'Z'
  title: string | null;
  status: 'scheduled' | 'cancelled';
  created_at: string | null;
  role: 'owner' | 'peer';        // relative to the viewer
  target_user_id: string | null; // the other party (whom you'd call)
  target_name: string | null;
  target_avatar_url: string | null;
}

/** Schedule a future 1:1 voice call. scheduled_at must be a true-UTC ISO string. */
export const scheduleCall = (body: {
  peer_user_id: string;
  scheduled_at: string;
  title?: string | null;
  conversation_id?: string | null;
}) => api.post<ScheduledCallRecord>('/calls/scheduled', body).then(r => r.data);

/** The viewer's upcoming scheduled calls, soonest first. */
export const listScheduledCalls = () =>
  api.get<ScheduledCallRecord[]>('/calls/scheduled').then(r => r.data);

/** Owner cancels a scheduled call. */
export const cancelScheduledCall = (id: string) =>
  api.delete(`/calls/scheduled/${encodeURIComponent(id)}`).then(() => undefined);
